import React from "react";
import styles from "./Button.module.scss";
import {
  PulseButton,
  PulseButtonTheme,
  PulseButtonSizes,
} from "@pulselabs-ai/shared-fe2";

export function Button() {
  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>PulseButton</div>
        </div>

        <div className={styles.description}>
          Buttons trigger an action such as submitting a form or showing/hiding
          an interface component.
        </div>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>Usage Guidance</div>
          <div className={styles.guidelinesList}>
            <ul>
              <li>Buttons should indicate an action.</li>
              <li>
                They should be discoverable, easy to identify, and specific.
              </li>
              <li>Make Buttons look and feel clickable.</li>
              <li>
                Icons can be used alone or added to the left or right of the
                label. If used, the icon should signify what the Button does.
              </li>
              <li>
                Use icon-only variants in dense environments or when space is
                limited.
              </li>
              <li>
                Use accessible tooltips with icon-only variants to help explain
                ambiguous icons for everyone.
              </li>
              <li>
                When deciding which Button to use, consider the level of
                priority of the action, as well as how much visual emphasis the
                Button should have in the context of the page it will live on.
                Be intentional and refer to the examples on the Button
                Properties and Button Instances pages to determine which is
                right for your use case.
              </li>
            </ul>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>When to Use Something Else</div>
          <div className={styles.guidelinesList}>
            <ul>
              <li>
                Use Hyperlinks within a paragraph to navigate to another page.
              </li>
              <li>
                Consider using{" "}
                <span className={styles.inlineLink}>checkbox</span>, a{" "}
                <span className={styles.inlineLink}>toggle</span>, or{" "}
                <span className={styles.inlineLink}>segmented control</span>{" "}
                when a component is needed that can capture 2 toggleable states.
              </li>
            </ul>
          </div>
        </section>

        {/* Button Grid */}
        <section className={styles.section}>
          <div className={styles.sectionTitle}>Button Variants</div>
          <div className={styles.description}>
            Our button system offers various themes, sizes, and states to
            support different use cases across the interface.
          </div>

          <div className={styles.buttonGrid}>
            {/* Column headers */}
            <div className={styles.gridHeader}>Primary</div>
            <div className={styles.gridHeader}>Secondary</div>
            <div className={styles.gridHeader}>Tertiary</div>
            <div className={styles.gridHeader}>Disabled</div>

            {/* Row 1: Extra Large buttons */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.EXTRA_LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY}
                size={PulseButtonSizes.EXTRA_LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY}
                size={PulseButtonSizes.EXTRA_LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.EXTRA_LARGE}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Row 2: Large buttons */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY}
                size={PulseButtonSizes.LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY}
                size={PulseButtonSizes.LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.LARGE}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Row 3: Medium buttons */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.MEDIUM}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Row 4: Small buttons */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.SMALL}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY}
                size={PulseButtonSizes.SMALL}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY}
                size={PulseButtonSizes.SMALL}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.SMALL}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Additional spacer */}
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>

            {/* Row 5: Primary color variations - Large */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_BLACK}
                size={PulseButtonSizes.LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_DESTRUCTIVE}
                size={PulseButtonSizes.LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_SUCCESS}
                size={PulseButtonSizes.LARGE}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_BLACK}
                size={PulseButtonSizes.LARGE}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Row 6: Primary color variations - Medium */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_BLACK}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_DESTRUCTIVE}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_SUCCESS}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_DESTRUCTIVE}
                size={PulseButtonSizes.MEDIUM}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Row 7: Primary color variations - Small */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_BLACK}
                size={PulseButtonSizes.SMALL}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_DESTRUCTIVE}
                size={PulseButtonSizes.SMALL}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_SUCCESS}
                size={PulseButtonSizes.SMALL}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY_SUCCESS}
                size={PulseButtonSizes.SMALL}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Additional spacer */}
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>

            {/* Row 8: Secondary color variations */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY_BLACK}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY_DESTRUCTIVE}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY_SUCCESS}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY_BLACK}
                size={PulseButtonSizes.MEDIUM}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Row 9: Tertiary color variations */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY_BLACK}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY_DESTRUCTIVE}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY_SUCCESS}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY_DESTRUCTIVE}
                size={PulseButtonSizes.MEDIUM}
                disabled
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Additional spacer */}
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>

            {/* Row 10: Link buttons */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.LINK}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.LINK_BLACK}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.LINK_DESTRUCTIVE}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.LINK_SUCCESS}
                size={PulseButtonSizes.MEDIUM}
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Additional spacer */}
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>
            <div className={styles.gridSpacer}></div>

            {/* Row 11: Special states */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.MEDIUM}
                loading
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.SECONDARY}
                size={PulseButtonSizes.MEDIUM}
                loading
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.TERTIARY}
                size={PulseButtonSizes.MEDIUM}
                loading
              >
                BUTTON
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.LINK}
                size={PulseButtonSizes.MEDIUM}
                loading
              >
                BUTTON
              </PulseButton>
            </div>

            {/* Row 12: Icon-only buttons */}
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.LARGE}
                // isOnlyIcon
              >
                +
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.MEDIUM}
                // isOnlyIcon
              >
                +
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.SMALL}
                // isOnlyIcon
              >
                +
              </PulseButton>
            </div>
            <div className={styles.gridCell}>
              <PulseButton
                theme={PulseButtonTheme.PRIMARY}
                size={PulseButtonSizes.MEDIUM}
                // isOnlyIcon
                disabled
              >
                +
              </PulseButton>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>Usage Examples</div>
          <div className={styles.description}>
            The PulseButton component supports various props to customize
            appearance and behavior:
          </div>

          <div className={styles.cssDetails}>
            <code className={styles.cssCode}>{`// Basic Button
<PulseButton>Click Me</PulseButton>

// Themed Button with Size
<PulseButton
  theme={PulseButtonTheme.PRIMARY_SUCCESS}
  size={PulseButtonSizes.LARGE}
>
  Submit
</PulseButton>

// Button Themes
<PulseButton theme={PulseButtonTheme.PRIMARY}>Primary</PulseButton>
<PulseButton theme={PulseButtonTheme.SECONDARY}>Secondary</PulseButton>
<PulseButton theme={PulseButtonTheme.TERTIARY}>Tertiary</PulseButton>
<PulseButton theme={PulseButtonTheme.LINK}>Link</PulseButton>

// Color Variations
<PulseButton theme={PulseButtonTheme.PRIMARY_BLACK}>Black</PulseButton>
<PulseButton theme={PulseButtonTheme.PRIMARY_DESTRUCTIVE}>Destructive</PulseButton>
<PulseButton theme={PulseButtonTheme.PRIMARY_SUCCESS}>Success</PulseButton>

// All Button Sizes
<PulseButton size={PulseButtonSizes.SMALL}>Small</PulseButton>
<PulseButton size={PulseButtonSizes.MEDIUM}>Medium (Default)</PulseButton>
<PulseButton size={PulseButtonSizes.LARGE}>Large</PulseButton>
<PulseButton size={PulseButtonSizes.EXTRA_LARGE}>Extra Large</PulseButton>

// Special States
<PulseButton disabled>Disabled Button</PulseButton>
<PulseButton loading>Loading Button</PulseButton>

// Icon-only Button
<PulseButton // isOnlyIcon>+</PulseButton>`}</code>
          </div>
        </section>
      </div>
    </div>
  );
}
